// src/components/Community.js
import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Users, MapPin, Waves, Calendar } from "lucide-react";
import HamburgerMenu from "./HamburgerMenu";
import DiveLoader from "./DiveLoader";
import UserLink from "./UserLink";
import PageHeader from "./PageHeader";
import ConditionsModal from "./ConditionsModal";
import ClubsSection from "./ClubsSection";
import CommunityStats from "./CommunityStats";
import DiversDirectory from "./DiversDirectory";
import { fetchWithAuth } from "../utils/api";
import "./Community.css";
import "./CommunityStats.css";

// Rampe "clarté de l'eau" (mêmes couleurs que le formulaire)
const VIS_COLORS = { 1: "#8a7a5c", 2: "#6f9e86", 3: "#3fa7b8", 4: "#1c9fd6", 5: "#0077b6" };
const VIS_DEPTH_BANDS = ["0-10", "10-20", "20-30", "30-40", "40+"];

// Médiane d'une liste de nombres (arrondie ; null si vide).
function median(nums) {
    const a = nums.map(Number).filter((n) => !Number.isNaN(n)).sort((x, y) => x - y);
    if (!a.length) return null;
    const mid = Math.floor(a.length / 2);
    return a.length % 2 ? a[mid] : Math.round((a[mid - 1] + a[mid]) / 2);
}

// Regroupe les rapports de conditions par site + jour, et agrège les avis
// (médiane de visibilité par profondeur + médiane de température) avec la liste des contributeurs.
function groupConditions(list) {
    const groups = new Map();
    for (const c of list || []) {
        const siteKey = c.site?.id || c.site?.name || "?";
        const key = `${siteKey}__${c.observed_date}`;
        if (!groups.has(key)) groups.set(key, { key, site: c.site, observed_date: c.observed_date, items: [] });
        groups.get(key).items.push(c);
    }
    return [...groups.values()].map((g) => {
        const visibility = {};
        for (const b of VIS_DEPTH_BANDS) {
            const levels = g.items.map((c) => c.visibility && c.visibility[b]).filter((v) => v != null);
            const m = median(levels);
            if (m != null) visibility[b] = m;
        }
        const water_temp = median(g.items.map((c) => c.water_temp).filter((v) => v != null));
        const reporters = [...new Set(g.items.map((c) => c.reporter?.first_name).filter(Boolean))];
        const notes = g.items.filter((c) => c.note).map((c) => ({ name: c.reporter?.first_name || "?", note: c.note }));
        return { key: g.key, site: g.site, observed_date: g.observed_date, visibility, water_temp, reporters, notes, count: g.items.length };
    });
}

export default function Community({ onLogout, currentUser }) {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const apiUrl = process.env.REACT_APP_API_URL;
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [recentConditions, setRecentConditions] = useState([]);
    const [showConditions, setShowConditions] = useState(false);
    const [expandedCond, setExpandedCond] = useState({});
    const [showAllWeek, setShowAllWeek] = useState(false);
    // Onglet actif dérivé de l'URL (?tab=) → le bouton retour du navigateur y revient.
    const [searchParams, setSearchParams] = useSearchParams();
    const VALID_TABS = ["week", "conditions", "clubs", "stats", "divers"];
    const urlTab = searchParams.get("tab");
    const tab = VALID_TABS.includes(urlTab) ? urlTab : "week";
    // replace par défaut (un clic d'onglet ne pollue pas l'historique) ;
    // push=true pour un drill-down (ex. clic brevet → annuaire) → le retour revient à l'onglet précédent.
    const setTab = (t, { push = false } = {}) => setSearchParams(
        (prev) => { const p = new URLSearchParams(prev); p.set("tab", t); return p; },
        { replace: !push }
    );
    const [directoryCert, setDirectoryCert] = useState(""); // filtre brevet pré-appliqué (deep-link depuis Chiffres)

    useEffect(() => {
        const loadStats = async () => {
            try {
                const res = await fetchWithAuth(`${apiUrl}/stats`);
                if (res.ok) {
                    const data = await res.json();
                    setStats(data);
                }
            } catch (err) {
                console.error("Erreur chargement stats:", err);
            } finally {
                setLoading(false);
            }
        };
        loadStats();
    }, [apiUrl]);

    const loadConditions = React.useCallback(async () => {
        try {
            const res = await fetchWithAuth(`${apiUrl}/site-conditions/recent`);
            if (res.ok) setRecentConditions(await res.json());
        } catch (err) {
            console.error("Erreur chargement conditions:", err);
        }
    }, [apiUrl]);

    useEffect(() => { loadConditions(); }, [loadConditions]);

    // "Qui plonge cette semaine" : plongées publiques dans les 7 prochains jours
    // On exclut les plongées déjà passées (date + heure < maintenant), pas seulement
    // par date : une plongée d'aujourd'hui dont l'heure est dépassée ne doit plus s'afficher.
    const now = new Date();
    const weekEnd = new Date();
    weekEnd.setHours(23, 59, 59, 999);
    weekEnd.setDate(weekEnd.getDate() + 7);
    const upcoming = (stats?.upcomingPublicDives || []).filter((d) => {
        if (!d.planned_date) return false;
        const timeStr = d.planned_time ? d.planned_time.slice(0, 5) : "23:59";
        return new Date(`${d.planned_date}T${timeStr}:00`) >= now;
    });
    const thisWeek = upcoming.filter((d) => new Date(d.planned_date) <= weekEnd);
    const laterCount = upcoming.length - thisWeek.length;

    const refreshStats = async () => {
        try {
            const statsRes = await fetchWithAuth(`${apiUrl}/stats`);
            if (statsRes.ok) setStats(await statsRes.json());
        } catch { /* ignore */ }
    };

    // Rejoindre une plongée
    const handleJoinDive = async (diveId) => {
        try {
            const res = await fetchWithAuth(`${apiUrl}/join-dive`, {
                method: "POST",
                body: JSON.stringify({ dive_intention_id: diveId }),
            });
            if (res.ok) refreshStats();
        } catch (err) {
            console.error("Erreur join dive:", err);
        }
    };

    // Se désinscrire d'une plongée
    const handleLeaveDive = async (diveId) => {
        try {
            const res = await fetchWithAuth(`${apiUrl}/unsubscribe-dive`, {
                method: "POST",
                body: JSON.stringify({ dive_intention_id: diveId }),
            });
            if (res.ok) refreshStats();
        } catch (err) {
            console.error("Erreur leave dive:", err);
        }
    };

    const formatWhen = (dive) => {
        const d = new Date(dive.planned_date).toLocaleDateString(i18n.language, {
            weekday: 'long',
            day: 'numeric',
            month: 'short',
        });
        return dive.planned_time ? `${d} · ${dive.planned_time.slice(0, 5)}` : d;
    };

    return (
        <div className="community-page">
            {/* Hamburger menu (desktop) */}
            <div style={{ position: "absolute", top: 14, right: 12, zIndex: 1500 }}>
                <HamburgerMenu
                    onLogout={onLogout}
                    onProfileClick={() => navigate("/profile")}
                    onAboutClick={() => navigate("/about")}
                    onMapClick={() => navigate("/")}
                    onCommunityClick={() => navigate("/community")}
                />
            </div>

            {/* En-tête (titre + vagues) */}
            <PageHeader title={t('community.title')} />

            <div className="community-body">
                {loading ? (
                    <DiveLoader />
                ) : stats ? (
                    <>
                        {/* KPIs compacts */}
                        <div className="kpi-strip">
                            <Kpi
                                icon={<Users size={18} />}
                                value={stats.totalUsers}
                                label={t('community.stats.members')}
                                sub={stats.newMembersThisMonth > 0 ? `+${stats.newMembersThisMonth} ${t('community.newMembers')}` : null}
                            />
                            <Kpi icon={<MapPin size={18} />} value={stats.totalSites} label={t('community.stats.sites')} />
                            <Kpi icon={<Waves size={18} />} value={stats.totalDives} label={t('community.stats.totalDives')} />
                            <Kpi
                                icon={<Calendar size={18} />}
                                value={stats.upcomingDives}
                                label={t('community.stats.upcoming')}
                                sub={stats.upcomingPrivateDives > 0 ? t('community.stats.upcomingPrivate', { count: stats.upcomingPrivateDives }) : null}
                            />
                        </div>

                        {/* Sous-onglets */}
                        <div className="community-pills">
                            <button className={`community-pill${tab === "week" ? " active" : ""}`} onClick={() => setTab("week")}>{t('community.tabs.week')}</button>
                            <button className={`community-pill${tab === "conditions" ? " active" : ""}`} onClick={() => setTab("conditions")}>{t('community.tabs.conditions')}</button>
                            <button className={`community-pill${tab === "clubs" ? " active" : ""}`} onClick={() => setTab("clubs")}>{t('community.tabs.clubs')}</button>
                            <button className={`community-pill${tab === "stats" ? " active" : ""}`} onClick={() => setTab("stats")}>{t('community.tabs.stats')}</button>
                            <button className={`community-pill${tab === "divers" ? " active" : ""}`} onClick={() => { setTab("divers"); setDirectoryCert(""); }}>{t('community.tabs.divers')}</button>
                        </div>

                        {tab === "week" && (
                        <>
                        {/* Qui plonge cette semaine */}
                        <h2 className="community-section-title" style={{ marginBottom: 4 }}>{t('community.thisWeek.title')}</h2>
                        <p className="community-subhint">{t('community.thisWeek.publicOnly')}</p>
                        {thisWeek.length > 0 ? (
                            <div className="week-list">
                                {(showAllWeek ? thisWeek : thisWeek.slice(0, 6)).map((dive) => {
                                    const divers = (dive.divers && dive.divers.length)
                                        ? dive.divers
                                        : [dive.organizer].filter(Boolean);
                                    return (
                                    <div key={dive.id} className="week-card">
                                        <div className="week-avatars">
                                            {divers.slice(0, 4).map((p, idx) => (
                                                <img
                                                    key={p.id || idx}
                                                    className="week-avatar"
                                                    src={p.profile_image_url || "/default-avatar.png"}
                                                    alt=""
                                                    style={{ marginLeft: idx === 0 ? 0 : -12, zIndex: 10 - idx }}
                                                />
                                            ))}
                                            {divers.length > 4 && (
                                                <span className="week-avatar-more">+{divers.length - 4}</span>
                                            )}
                                        </div>
                                        <div className="week-info">
                                            <div className="week-site">{dive.site?.name || "?"}</div>
                                            <div className="week-meta">
                                                <span className="week-when">{formatWhen(dive)}</span>
                                                {divers.length > 0 && (
                                                    <> · {divers.filter((p) => p.first_name).slice(0, 3).map((p, i) => (
                                                        <React.Fragment key={p.id || i}>{i > 0 ? ", " : ""}<UserLink id={p.id} firstName={p.first_name} /></React.Fragment>
                                                    ))}{divers.length > 3 ? "…" : ""}</>
                                                )}
                                            </div>
                                        </div>
                                        {dive.is_organizer ? (
                                            <span className="week-icon-btn week-icon-org" title={t('community.thisWeek.organizer')} aria-label={t('community.thisWeek.organizer')}>
                                                <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                                                    <path d="M12 2l2.9 6.26L22 9.27l-5 4.87L18.18 21 12 17.27 5.82 21 7 14.14l-5-4.87 7.1-1.01L12 2z" />
                                                </svg>
                                            </span>
                                        ) : dive.is_participant ? (
                                            <button className="week-icon-btn week-icon-joined" onClick={() => handleLeaveDive(dive.id)} title={t('community.thisWeek.leave')} aria-label={t('community.thisWeek.leave')}>
                                                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                                    <polyline points="20 6 9 17 4 12" />
                                                </svg>
                                            </button>
                                        ) : (
                                            <button className="week-icon-btn week-icon-join" onClick={() => handleJoinDive(dive.id)} title={t('community.upcomingPublic.join')} aria-label={t('community.upcomingPublic.join')}>
                                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                                    <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
                                                </svg>
                                            </button>
                                        )}
                                    </div>
                                    );
                                })}
                                {!showAllWeek && thisWeek.length > 6 && (
                                    <button className="week-showall" onClick={() => setShowAllWeek(true)}>
                                        {t('community.thisWeek.showAll', { count: thisWeek.length - 6 })}
                                    </button>
                                )}
                                {laterCount > 0 && (
                                    <div style={{ fontSize: "0.85rem", color: "#8a97a3", textAlign: "center", marginTop: 2 }}>
                                        {t('community.thisWeek.more', { count: laterCount })}
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="week-empty">
                                <p style={{ margin: "0 0 14px" }}>{t('community.thisWeek.empty')}</p>
                                <button className="week-empty-cta" onClick={() => navigate("/?create=1")}>
                                    {t('community.thisWeek.cta')}
                                </button>
                            </div>
                        )}
                        </>
                        )}

                        {tab === "conditions" && (
                        <>
                        {/* Conditions récentes */}
                        <div className="cond-section-head">
                            <h2 className="community-section-title" style={{ margin: 0 }}>{t('conditions.recentTitle')}</h2>
                            <button className="cond-report-btn" onClick={() => setShowConditions(true)}>
                                + {t('conditions.reportButton')}
                            </button>
                        </div>
                        <p className="community-subhint">{t('conditions.recentHint')}</p>
                        {recentConditions.length > 0 ? (
                            <div className="cond-feed">
                                {groupConditions(recentConditions).slice(0, 8).map((c) => {
                                    const open = !!expandedCond[c.key];
                                    return (
                                    <div key={c.key} className="cond-feed-card">
                                        <button
                                            className="cond-feed-head"
                                            onClick={() => setExpandedCond((e) => ({ ...e, [c.key]: !e[c.key] }))}
                                            aria-expanded={open}
                                        >
                                            <span className="cond-feed-site">{c.site?.name || "?"}{c.count > 1 ? ` · ×${c.count}` : ""}</span>
                                            <span className="cond-feed-right">
                                                <span className="cond-feed-date">{formatConditionDate(c.observed_date, i18n.language)}</span>
                                                <svg className={`cond-feed-chevron${open ? " open" : ""}`} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                                    <polyline points="6 9 12 15 18 9" />
                                                </svg>
                                            </span>
                                        </button>
                                        {open && (
                                            <div className="cond-feed-details">
                                                {c.count > 1 && <div style={{ fontSize: 11.5, color: "#7d95a3", marginBottom: 6, fontStyle: "italic" }}>{t('conditions.medianHint', { count: c.count })}</div>}
                                                <div className="cond-column">
                                                    {VIS_DEPTH_BANDS.map((b, i) => {
                                                        const lvl = c.visibility && c.visibility[b];
                                                        return (
                                                            <div className="cond-col-row" key={b}>
                                                                <span
                                                                    className="cond-col-seg"
                                                                    style={{
                                                                        background: lvl ? VIS_COLORS[lvl] : "#eef2f4",
                                                                        borderTopLeftRadius: i === 0 ? 6 : 0,
                                                                        borderTopRightRadius: i === 0 ? 6 : 0,
                                                                        borderBottomLeftRadius: i === VIS_DEPTH_BANDS.length - 1 ? 6 : 0,
                                                                        borderBottomRightRadius: i === VIS_DEPTH_BANDS.length - 1 ? 6 : 0,
                                                                    }}
                                                                />
                                                                <span className="cond-col-depth">{b === "40+" ? "40+ m" : `${b} m`}</span>
                                                                <span className="cond-col-lvl">{lvl ? t(`conditions.levels.${lvl}`) : "—"}</span>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                                {c.water_temp != null && (
                                                    <div className="cond-col-temp">{t('conditions.tempShort')} : {c.water_temp} °C</div>
                                                )}
                                                {c.notes.map((n, i) => (
                                                    <div key={i} className="cond-feed-note">{c.count > 1 ? `${n.name} : ${n.note}` : n.note}</div>
                                                ))}
                                                <div className="cond-feed-by">{c.count > 1 ? t('conditions.reportedBy', { names: c.reporters.join(", ") }) : (c.reporters[0] || "?")}</div>
                                            </div>
                                        )}
                                    </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="week-empty" style={{ marginBottom: 4 }}>{t('conditions.recentEmpty')}</div>
                        )}
                        </>
                        )}

                        {tab === "clubs" && (
                            <ClubsSection apiUrl={apiUrl} />
                        )}

                        {/* Chiffres : scène sous-marine (composant dédié) */}
                        {tab === "stats" && (
                            <CommunityStats
                                stats={stats}
                                t={t}
                                onPickCert={(cert) => { setDirectoryCert(cert); setTab("divers", { push: true }); }}
                            />
                        )}

                        {/* Annuaire des plongeurs */}
                        {tab === "divers" && (
                            <DiversDirectory apiUrl={apiUrl} t={t} initialCert={directoryCert} />
                        )}
                    </>
                ) : (
                    <p style={{ padding: 20 }}>{t('community.error')}</p>
                )}
            </div>

            {showConditions && (
                <ConditionsModal
                    apiUrl={apiUrl}
                    onClose={() => setShowConditions(false)}
                    onSaved={loadConditions}
                />
            )}
        </div>
    );
}

// Date relative pour le fil de conditions ("aujourd'hui", "il y a 3 j", ou date).
function formatConditionDate(dateStr, lang) {
    if (!dateStr) return "";
    const d = new Date(dateStr + "T00:00:00");
    const now = new Date();
    const startNow = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const days = Math.round((startNow - d) / 86400000);
    const l = (lang || "fr").slice(0, 2);
    if (days <= 0) return l === "de" ? "heute" : l === "en" ? "today" : "aujourd'hui";
    if (days === 1) return l === "de" ? "gestern" : l === "en" ? "yesterday" : "hier";
    if (days < 14) return l === "en" ? `${days} days ago` : l === "de" ? `vor ${days} Tagen` : `il y a ${days} j`;
    return d.toLocaleDateString(lang, { day: "numeric", month: "short", year: "numeric" });
}

// Petite pastille KPI compacte
function Kpi({ icon, value, label, sub }) {
    return (
        <div className="kpi-chip">
            <div style={{ color: "var(--primary-blue)", display: "flex", justifyContent: "center", marginBottom: 2 }}>
                {icon}
            </div>
            <div className="kpi-value">{value}</div>
            <div className="kpi-label">{label}</div>
            {sub && <div className="kpi-sub">{sub}</div>}
        </div>
    );
}
